import { PassportLogo } from "@/components/Reveal";
import { Link } from "react-router-dom";
import { InstagramLogo, TwitterLogo, TiktokLogo, LinkedinLogo, YoutubeLogo, ArrowUpRight } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

const ROUTES = {
    "Privacy Policy": "/privacy",
    "Terms of Service": "/terms",
    "Delete My Account": "/delete-account",
    "Pricing": "/#pricing",
    "Features": "/#manifesto",
    "Business Solutions": "/#business",
};

const GROUP_KEYS = [
    {
        hKey: "footer.groups.Product.title",
        links: [
            { key: "footer.groups.Product.links.Features", defaultLabel: "Features" },
            { key: "footer.groups.Product.links.Pricing", defaultLabel: "Pricing" },
            { key: "footer.groups.Product.links.Business Solutions", defaultLabel: "Business Solutions" },
            { key: "footer.groups.Product.links.Developers / API", defaultLabel: "Developers / API" },
            { key: "footer.groups.Product.links.Travel Guides", defaultLabel: "Travel Guides" },
            { key: "footer.groups.Product.links.Blog", defaultLabel: "Blog" },
        ],
    },
    {
        hKey: "footer.groups.Company.title",
        links: [
            { key: "footer.groups.Company.links.About", defaultLabel: "About" },
            { key: "footer.groups.Company.links.Careers", defaultLabel: "Careers" },
            { key: "footer.groups.Company.links.Press", defaultLabel: "Press" },
            { key: "footer.groups.Company.links.Partners", defaultLabel: "Partners" },
            { key: "footer.groups.Company.links.Sustainability", defaultLabel: "Sustainability" },
            { key: "footer.groups.Company.links.Contact", defaultLabel: "Contact" },
        ],
    },
    {
        hKey: "footer.groups.Support.title",
        links: [
            { key: "footer.groups.Support.links.Help Center", defaultLabel: "Help Center" },
            { key: "footer.groups.Support.links.Community Rules", defaultLabel: "Community Rules" },
            { key: "footer.groups.Support.links.Trust & Safety", defaultLabel: "Trust & Safety" },
            { key: "footer.groups.Support.links.Privacy Policy", defaultLabel: "Privacy Policy" },
            { key: "footer.groups.Support.links.Terms of Service", defaultLabel: "Terms of Service" },
            { key: "footer.groups.Support.links.Delete My Account", defaultLabel: "Delete My Account" },
        ],
    },
];

function FooterLink({ labelKey, defaultLabel }) {
    const { t } = useTranslation();
    const label = t(labelKey);
    const to = ROUTES[defaultLabel];
    const cls = "text-sm text-white/70 hover:text-[color:var(--gold)] transition-colors inline-flex items-center gap-1 group";
    const inner = (
        <>
            {label}
            <ArrowUpRight size={12} className="opacity-0 group-hover:opacity-100 transition-opacity" />
        </>
    );
    if (to && to.startsWith("/") && !to.startsWith("/#")) {
        return <Link to={to} className={cls} data-testid={`footer-${defaultLabel.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{inner}</Link>;
    }
    return <a href={to || "#"} className={cls} data-testid={`footer-${defaultLabel.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{inner}</a>;
}

export default function Footer() {
    const { t } = useTranslation();
    return (
        <footer className="relative border-t pt-24 pb-12" style={{ borderColor: "rgba(255,255,255,0.06)", background: "var(--navy-deep)" }} data-testid="footer">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <div className="grid lg:grid-cols-[1.3fr_2fr] gap-16 pb-16">
                    <div>
                        <div className="flex items-center gap-3 mb-6">
                            <PassportLogo size={44} />
                            <div>
                                <div className="font-serif text-2xl text-white leading-none">Passport</div>
                                <div className="overline mt-1" style={{ fontSize: "9px" }}>{t("footer.tagline")}</div>
                            </div>
                        </div>

                        <p className="text-white/60 text-sm leading-relaxed max-w-xs mb-8">
                            {t("footer.description")}
                        </p>

                        <div className="space-y-2 text-xs text-white/50 leading-relaxed">
                            <div className="text-white/80 uppercase tracking-[0.2em] text-[10px] mb-2">{t("footer.headquarters.title")}</div>
                            <div>{t("footer.headquarters.name")}</div>
                            <div>{t("footer.headquarters.address1")}</div>
                            <div>{t("footer.headquarters.address2")}</div>
                        </div>

                        <div className="mt-8 space-y-2 text-xs">
                            <div className="text-white/80 uppercase tracking-[0.2em] text-[10px] mb-2">{t("footer.contact.title")}</div>
                            <div className="text-white/60">
                                {t("footer.contact.general")} <a href="mailto:support@passporttravelnetwork.com" className="text-white hover:text-[color:var(--gold)]">support@passporttravelnetwork.com</a>
                            </div>
                            <div className="text-white/60">
                                {t("footer.contact.customer")} <a href="mailto:help@passporttravelnetwork.com" className="text-white hover:text-[color:var(--gold)]">help@passporttravelnetwork.com</a>
                            </div>
                            <div className="text-white/60">{t("footer.contact.phone")} <span className="text-white">(785) 506-1375</span></div>
                        </div>

                        <div className="mt-8 space-y-1 text-xs text-white/50">
                            <div className="text-white/80 uppercase tracking-[0.2em] text-[10px] mb-2">{t("footer.hours.title")}</div>
                            <div>{t("footer.hours.weekday")}</div>
                            <div>{t("footer.hours.saturday")}</div>
                            <div>{t("footer.hours.sunday")}</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
                        {GROUP_KEYS.map((g) => (
                            <div key={g.hKey}>
                                <div className="overline mb-5">{t(g.hKey)}</div>
                                <ul className="space-y-3">
                                    {g.links.map((l) => (
                                        <li key={l.defaultLabel}>
                                            <FooterLink labelKey={l.key} defaultLabel={l.defaultLabel} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Big brand mark */}
                <div className="relative py-10 select-none pointer-events-none">
                    <div className="font-serif text-white/[0.06] tracking-tighter leading-none whitespace-nowrap" style={{ fontSize: "clamp(6rem, 20vw, 20rem)" }}>
                        Passport
                    </div>
                </div>

                <div className="pt-8 border-t flex flex-col md:flex-row items-start md:items-center justify-between gap-6" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                    <div className="text-xs text-white/40">{t("footer.rights", { year: new Date().getFullYear() })}</div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
                        <Link to="/privacy" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="footer-bottom-privacy">{t("footer.bottomLinks.privacy")}</Link>
                        <Link to="/terms" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="footer-bottom-terms">{t("footer.bottomLinks.terms")}</Link>
                        <Link to="/delete-account" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="footer-bottom-delete">{t("footer.bottomLinks.delete")}</Link>
                        <a href="mailto:support@passporttravelnetwork.com" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="footer-bottom-contact">{t("footer.bottomLinks.contact")}</a>
                        <a href="mailto:help@passporttravelnetwork.com" className="text-white/60 hover:text-[color:var(--gold)]" data-testid="footer-bottom-support">{t("footer.bottomLinks.support")}</a>
                    </div>
                    <div className="flex items-center gap-4">
                        {[InstagramLogo, TwitterLogo, TiktokLogo, LinkedinLogo, YoutubeLogo].map((I, i) => (
                            <a key={i} href="#" className="w-9 h-9 rounded-full grid place-items-center border transition-colors duration-500 hover:bg-white/5" style={{ borderColor: "rgba(255,255,255,0.1)" }} aria-label="social">
                                <I size={14} weight="regular" color="rgba(255,255,255,0.8)" />
                            </a>
                        ))}
                    </div>
                </div>
            </div>
        </footer>
    );
}
